// ===== K-Tec Conversation Memory =====
// Maintains context during a chat session

import type { ParsedRequirement } from './requirementParser';
import type { Language } from './languageDetector';

export interface ConversationState {
  id: string;
  language: Language;
  requirement: ParsedRequirement;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerAddress?: string;
  conversationPhase: 'greeting' | 'discovery' | 'recommendation' | 'quotation' | 'closed';
  questionsAsked: string[];
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
  estimatedValue?: number;
  selectedProducts?: string[];
  isLead: boolean;
  leadCreated: boolean;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  type?: 'text' | 'recommendation' | 'solution' | 'quotation-form' | 'products';
  data?: Record<string, unknown>;
}

export function createConversationState(): ConversationState {
  return {
    id: `conv-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
    language: 'english',
    requirement: {},
    conversationPhase: 'greeting',
    questionsAsked: [],
    messages: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isLead: false,
    leadCreated: false,
  };
}

export function updateConversation(
  state: ConversationState,
  updates: Partial<ConversationState>
): ConversationState {
  return {
    ...state,
    ...updates,
    updatedAt: new Date().toISOString(),
  };
}

export function addMessage(
  state: ConversationState,
  message: Omit<ChatMessage, 'id' | 'timestamp'>
): ConversationState {
  const newMessage: ChatMessage = {
    ...message,
    id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 5)}`,
    timestamp: new Date().toISOString(),
  };
  return {
    ...state,
    messages: [...state.messages, newMessage],
    updatedAt: new Date().toISOString(),
  };
}

export function hasAskedQuestion(state: ConversationState, question: string): boolean {
  return state.questionsAsked.includes(question);
}

export function markQuestionAsked(state: ConversationState, question: string): ConversationState {
  if (state.questionsAsked.includes(question)) return state;
  return { ...state, questionsAsked: [...state.questionsAsked, question] };
}

export function getConversationSummary(state: ConversationState): string {
  const { requirement, customerName, customerPhone, language, estimatedValue } = state;
  const parts: string[] = [];
  if (customerName) parts.push(`Customer: ${customerName}`);
  if (customerPhone) parts.push(`Phone: ${customerPhone}`);
  if (language) parts.push(`Language: ${language}`);
  if (requirement.propertyType) parts.push(`Property: ${requirement.propertyType}`);
  if (requirement.serviceType) parts.push(`Service: ${requirement.serviceType}`);
  if (requirement.cameraQuantity) parts.push(`Cameras: ${requirement.cameraQuantity}`);
  if (requirement.fullColour) parts.push('Full Colour');
  if (requirement.budget) parts.push(`Budget: LKR ${requirement.budget.toLocaleString()}`);
  if (estimatedValue) parts.push(`Estimated: LKR ${estimatedValue.toLocaleString()}`);
  return parts.join(' | ');
}
